/**
 * InjectedDependencies - module resolution for bundled game code.
 * Provides CommonJS-style require() that resolves engine, three, and addon imports.
 */

import * as THREE from 'three';
// Import commonly used Three.js examples directly for reliable access
import * as TSL from 'three/tsl';
import * as WEBGPU from 'three/webgpu';

/**
 * Creates a lazy proxy for the ENGINE namespace.
 * This proxy accesses the ENGINE module at property access time (via require),
 * ensuring that circular dependencies have been resolved before any property is read.
 */
function createEngineProxy(): any {
  return new Proxy(
    {},
    {
      get(_target, prop: string | symbol) {
        // Access ENGINE through require at access time to get fully resolved module
        // This ensures circular dependencies are resolved before any property is read
        const ENGINE = require('../index.js');
        return ENGINE[prop];
      },
    }
  );
}

/**
 * Creates an injected dependencies function for CommonJS module resolution.
 * This function resolves module imports for '@gnsx/genesys.js', 'three', and Three.js examples.
 *
 * @param engineModule - Optional engine module to use instead of the default import
 * @returns A function that takes a module name and returns the corresponding module
 */
export function createInjectedDependencies(engineModule?: any): (mod: string) => any {
  // Use a lazy proxy that accesses ENGINE at property access time.
  // This ensures circular dependencies are resolved before any class is accessed.
  const resolvedEngine = engineModule ?? createEngineProxy();

  return (mod: string) => {
    if (mod === 'genesys.js' || mod === '@gnsx/genesys.js' || mod === '@directivegames/genesys.js') return resolvedEngine;
    if (mod === 'three') return THREE;
    if (mod === 'three/tsl') return TSL;
    if (mod === 'three/webgpu') return WEBGPU;

    throw new Error(`Unknown module: ${mod}. Supported modules: '@gnsx/genesys.js', 'three', 'three/tsl', 'three/webgpu'`);
  };
}
